import {useState} from 'react'
import {Map, List} from 'immutable'
import PropTypes from 'prop-types'

import AuthenticatorAppIcon from '../../AuthenticatorAppIcon'
import BackupCodeIcon from '../../BackupCodeIcon'
import {Button} from '../../../../../dashboard/src/components/blocks/Buttons'
import {ContentBox} from '../../../../../dashboard/src/components/blocks/ContentBoxes'
import TfaEnrollmentDetails from './TfaEnrollmentDetails'
import Loader from '../../../../../shared_components/NewLoader'
import SmartphoneIcon from '../tfa_setup/SmartphoneIcon'
import {TextGutterMedium, Text} from '../../../../../dashboard/src/components/blocks/Texts'
import {BoxGutterMedium, Box} from '../../../../../dashboard/src/components/blocks/Boxes'
import UnorderedList from '../../../../../shared_components/UnorderedList'

import {pluralize} from '../../../../../lib/tools'

import './tfaInfoDisplay.scss'

const TfaInfoDisplay = props => {
  const {loading, backupCodes, appEnrollment, phoneEnrollment} = props
  const [creatingAppEnrollment, setCreatingAppEnrollment] = useState(false)

  const manageRecoveryCodes = () => {
    props.manageTfaSetup('recovery')
  }

  const managePhoneEnrollment = () => {
    props.manageTfaSetup('phone')
  }

  const manageAppEnrollment = () => {
    if (appEnrollment.isEmpty()) {
      setCreatingAppEnrollment(true)

      props.createAppEnrollment(() => {
        props.manageTfaSetup('app')
        setCreatingAppEnrollment(false)
      })
    } else {
      props.manageTfaSetup('app')
    }
  }

  return (
    <Loader loading={loading}>
      <div className='tfa-info'>
        <h1 className='title'>Two-factor authentication</h1>
        <TextGutterMedium className='subtitle'>Manage your authentication methods to keep your account secure.</TextGutterMedium>
        <Box className='note'>
          <h4>Security tips</h4>
          <UnorderedList
            children={[
              'Authenticator apps are more secure than SMS',
              'Enable SMS method to prevent lockout when switching devices',
              'Keep your backup codes in a safe place'
            ]}
          />
        </Box>
        <BoxGutterMedium className='tfa-enrollments'>
          <ContentBox className='tfa-enrollments-header'>
            <h2>Authentication methods</h2>
            <Text>Choose how you want to verify your identity when signing in</Text>
          </ContentBox>
          <TfaEnrollmentDetails
            loading={creatingAppEnrollment}
            name={appEnrollment.get('name') || 'Authenticator app'}
            primary={appEnrollment.get('category') === 'primary'}
            instruction='Use an app like Google Authenticator or Authy to generate verification codes'
            tfaEnrollment={appEnrollment}
            buttonText={appEnrollment.isEmpty() ? 'Set up' : 'Manage'}
            icon={<AuthenticatorAppIcon />}
            primaryAction={manageAppEnrollment}
          />
          <TfaEnrollmentDetails
            name={phoneEnrollment.get('name') || 'Phone (SMS/Voice)'}
            instruction='Receive verification codes via message to your phone'
            tfaEnrollment={phoneEnrollment}
            primary={phoneEnrollment.get('category') === 'primary'}
            buttonText={phoneEnrollment.isEmpty() ? 'Set up' : 'Manage'}
            icon={<SmartphoneIcon />}
            primaryAction={managePhoneEnrollment}
          />
        </BoxGutterMedium>
        {
          props.tfaConfig.get('status') === 'enabled' && (
            <BoxGutterMedium className='tfa-enrollments-details flex-container'>
              <ContentBox className='icon-container'>
                <BackupCodeIcon />
              </ContentBox>
              <ContentBox className='enrollments-container recovery'>
                <h3>Backup codes</h3>
                <TextGutterMedium className='instruction'>Use backup codes to access your account if you lose access to your authentication methods</TextGutterMedium>
                <TextGutterMedium className='backup-codes-count'>{backupCodes.size} {pluralize('code', backupCodes.size)} remaining</TextGutterMedium>
                <Button onClick={manageRecoveryCodes}>Manage</Button>
              </ContentBox>
            </BoxGutterMedium>
          )
        }
      </div>
    </Loader>
  )
}

TfaInfoDisplay.propTypes = {
  appEnrollment: PropTypes.instanceOf(Map),
  createAppEnrollment: PropTypes.func,
  phoneEnrollment: PropTypes.instanceOf(Map),
  backupCodes: PropTypes.instanceOf(List),
  tfaConfig: PropTypes.instanceOf(Map),
  organizationConfig: PropTypes.instanceOf(Map),
  loading: PropTypes.bool,
  isFuneralHome: PropTypes.bool,
  isFirmUser: PropTypes.bool,
  isQuarterback: PropTypes.bool,
  manageTfaSetup: PropTypes.func
}

export default TfaInfoDisplay
